import { useState, type FC } from "react";
import { Button, Drawer, message as antdMessage } from "antd";
import { DownloadOutlined } from "@ant-design/icons";

import { ehrRecordsController } from "../../../AppController";
import {
  parseEhrLabDocumentRef,
  resolveEhrLabDownloadUrl,
  type EhrLabDocumentRef,
} from "../labDocumentRef";
import { trackNeoEvent } from "../neoAnalytics";
import { useNeoClinicWord } from "../neoClinicWord";
import {
  buildSourceHash,
  parseSourceSnippet,
  SOURCE_KIND_LABEL,
  sourceCtaLabel,
  type NeoSource,
} from "./neoSources";
import styles from "./SourceDrawer.module.scss";

/** One citation row inside the drawer: the entity's short-form identity, any
 *  concrete excerpt, and its OWN navigation button — so when the drawer shows
 *  several sources each stays clearly tied to its own link. */
const SourceCard: FC<{
  source: NeoSource;
  onGo: (src: NeoSource) => void;
  onDownload: (ref: EhrLabDocumentRef) => Promise<void>;
}> = ({ source, onGo, onDownload }) => {
  // Re-render this card's copy when the care setting resolves.
  useNeoClinicWord();
  const [downloadPending, setDownloadPending] = useState(false);
  const snippet = source.snippet ? parseSourceSnippet(source.snippet) : "";
  const hash = buildSourceHash(source);
  const documentRef = parseEhrLabDocumentRef(source.document_ref);
  const subtitle =
    source.source_type !== "patient" &&
    source.source_type !== "clinic" &&
    source.patient_display
      ? source.patient_display
      : "";
  return (
    <li className={styles.sourceCard} data-neo-type={source.source_type}>
      <p className={styles.sourceCardMeta}>
        {SOURCE_KIND_LABEL[source.source_type] ?? source.source_type}
        {subtitle ? ` · ${subtitle}` : ""}
      </p>
      <p className={styles.sourceCardTitle}>{source.title}</p>
      {snippet && <p className={styles.sourceDrawerSnippet}>{snippet}</p>}
      {(hash || documentRef) && (
        <div className={styles.sourceCardActions}>
          {hash && (
            <Button size="small" onClick={() => onGo(source)}>
              {sourceCtaLabel(source.source_type)}
            </Button>
          )}
          {documentRef && (
            <Button
              size="small"
              icon={<DownloadOutlined />}
              loading={downloadPending}
              onClick={() => {
                setDownloadPending(true);
                void onDownload(documentRef).finally(() =>
                  setDownloadPending(false)
                );
              }}
            >
              Download
            </Button>
          )}
        </div>
      )}
    </li>
  );
};

const SourceDrawer: FC<{
  sources: NeoSource[];
  claim?: string;
  onClose: () => void;
  /** Extra class on the drawer root (the 2026 UI goes full width on phones). */
  rootClassName?: string;
}> = ({ sources, claim, onClose, rootClassName }) => {
  const open = sources.length > 0;
  const handleGo = (src: NeoSource) => {
    const hash = buildSourceHash(src);
    if (!hash) return;
    trackNeoEvent("source navigation", {
      source_type: src.source_type,
      source_id: src.source_id,
      destination_hash: hash,
    });
    window.location.hash = hash;
    onClose();
  };
  const handleDownload = async (ref: EhrLabDocumentRef) => {
    try {
      const downloadUrl = await resolveEhrLabDownloadUrl(
        ref,
        ehrRecordsController.downloadEhrLab
      );
      const anchor = document.createElement("a");
      anchor.href = downloadUrl;
      anchor.target = "_blank";
      anchor.rel = "noopener noreferrer";
      anchor.click();
      trackNeoEvent("source document download", {
        source_type: "lab",
        clinic_id: ref.clinic_id,
        patient_id: ref.patient_id,
        lab_id: ref.lab_id,
      });
    } catch (error) {
      console.error("[Neo] lab source download failed", error);
      antdMessage.error(
        "The report could not be downloaded. Please try again."
      );
    }
  };
  const title = sources.length <= 1 ? "Source" : `${sources.length} Sources`;
  const handleDrawerClose = () => {
    trackNeoEvent("source drawer closed", { source_count: sources.length });
    onClose();
  };

  return (
    <Drawer
      open={open}
      onClose={handleDrawerClose}
      title={title}
      width={460}
      rootClassName={rootClassName}
    >
      {open && (
        <div className={styles.sourceDrawerBody}>
          {/* The claim from the chat these sources are cited as evidence for. */}
          {claim && (
            <div className={styles.sourceDrawerClaim}>
              <span className={styles.sourceDrawerClaimLabel}>
                Cited in support of
              </span>
              <blockquote className={styles.sourceDrawerClaimText}>
                {claim}
              </blockquote>
            </div>
          )}

          <ul className={styles.sourceCardList}>
            {sources.map((src) => (
              <SourceCard
                key={src.source_id}
                source={src}
                onGo={handleGo}
                onDownload={handleDownload}
              />
            ))}
          </ul>
        </div>
      )}
    </Drawer>
  );
};

export default SourceDrawer;
